import { useEffect, useState } from "react";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { inputClass } from "../components/FormKit";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { labelize } from "../lib/format";
import { listDisputes, resolveDispute, RESOLUTION_ACTIONS, type Dispute } from "../lib/disputes";

const TABS = [
  { key: "open", label: "Open" },
  { key: "under_review", label: "Under review" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
];

export function AdminDisputes() {
  const { roles } = useAuth();
  const isAdmin = can(roles, "admin_moderation");
  const [tab, setTab] = useState("open");
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [draft, setDraft] = useState<Record<string, { action: string; resolution: string }>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh(status = tab) {
    const r = await listDisputes(status);
    setDisputes(r.disputes);
    setCounts(r.counts);
  }
  useEffect(() => { if (isAdmin) void refresh(tab); }, [tab, isAdmin]);

  async function decide(d: Dispute, target: "resolved" | "dismissed") {
    setBusy(d.id + target);
    try {
      const dr = draft[d.id] ?? { action: "refund", resolution: "" };
      await resolveDispute(d.id, target, dr.resolution || undefined, target === "resolved" ? dr.action : undefined);
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Disputes" subtitle="Admin access required." />
        <p className="font-mono text-sm text-danger">Admins only.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader title="Admin · Disputes" subtitle="Mediate and resolve disputes between parties." />
      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}>
            {t.label} ({counts[t.key] ?? 0})
          </button>
        ))}
      </div>

      {disputes.length === 0 ? (
        <p className="font-mono text-sm text-muted">Nothing here.</p>
      ) : (
        <ul className="grid gap-3">
          {disputes.map((d) => {
            const dr = draft[d.id] ?? { action: "refund", resolution: "" };
            const closed = d.status === "resolved" || d.status === "dismissed";
            return (
              <li key={d.id} className="grid gap-3 rounded-card border border-line bg-surface p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold">{d.claim}</p>
                    <p className="font-mono text-[11px] text-muted">
                      {labelize(d.context_type)}{d.context_id ? ` · ${d.context_id.slice(0, 12)}` : ""} · opened by {d.opener_email ?? "—"} · {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <StatusLabel status={d.status} />
                </div>
                {!closed && (
                  <div className="grid gap-2 border-t border-line pt-3 sm:grid-cols-[160px_1fr_auto]">
                    <select className={inputClass} value={dr.action}
                      onChange={(e) => setDraft((p) => ({ ...p, [d.id]: { ...dr, action: e.target.value } }))} aria-label="Resolution action">
                      {RESOLUTION_ACTIONS.filter((a) => a !== "none").map((a) => <option key={a} value={a}>{labelize(a)}</option>)}
                    </select>
                    <input className={inputClass} placeholder="Outcome summary" value={dr.resolution}
                      onChange={(e) => setDraft((p) => ({ ...p, [d.id]: { ...dr, resolution: e.target.value } }))} />
                    <div className="flex gap-1.5">
                      <Button variant="ghost" disabled={busy === d.id + "resolved"} onClick={() => decide(d, "resolved")}>Resolve</Button>
                      <Button variant="ghost" disabled={busy === d.id + "dismissed"} onClick={() => decide(d, "dismissed")}>Dismiss</Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
